import React, { useMemo } from 'react';
import {
 Flex,
 Stack,
 Checkbox,
 Text,
 RadioGroup,
 Radio,
 Box,
 Button,
 Table,
 Thead,
 Tbody,
 Tr,
 Th,
 Td,
} from '@chakra-ui/react';
import { QuestionHelper } from '../Data';
import RotuloClassificacao from '../Catalog/RotuloClassificacao';
import { NumericFormat } from 'react-number-format';
import ChatAnswersPopover from '../Feedback/Chat';
import FeedbackChatReviewerPopover from '../Feedback/Chat-reviewer';
import MedidasGri from '../Catalog/MedidasGri';
import QuestionScoreField from './QuestionScoreField';
import QuestionEvidenceVerdict from './QuestionEvidenceVerdict';
import { FiPaperclip } from 'react-icons/fi';
import { useAuditMode } from '../../context/AuditMode';
import { AUDIT, AUDIT_LABEL, corDoRail } from '../../theme/auditTokens';
import { respostaTemMedida } from '../../helpers/medidasGri';

// EG-63: opções escolhidas viram selos estáticos em modo auditoria — radio/
// checkbox readOnly parecem interativos e distraem quem só está conferindo.
function SelectedChips({ options }) {
 if (!options?.length) {
  return (
   <Text fontSize='13px' color={AUDIT.inkFaint} fontStyle='italic'>
    Nenhuma opção selecionada
   </Text>
  );
 }
 return (
  <Flex gap='8px' flexWrap='wrap'>
   {options.map((o, i) => (
    <Box
     key={i}
     px='10px'
     py='5px'
     bg={AUDIT.accentSoft}
     color={AUDIT.accentInk}
     borderRadius='999px'
     fontSize='13px'
     fontWeight={600}
    >
     {o.description}
    </Box>
   ))}
  </Flex>
 );
}

const QuestionCardSimple = React.memo(
 ({
  question,
  selectedCompanyId,
  handleQuestionEvidence,
  surveyId,
  userId,
  questionnaireType,
  themeId,
  openChat,
  isResponsibleUser = false,
  index,
  ...rest
 }) => {
  const { code, description, type, helper } = question;
  // Só a tela de auditoria liga esse contexto; nas demais o card segue igual.
  const auditMode = useAuditMode();
  // EG-63: `hideInlineHeader` só existe no provider da /audit/view.
  const isAudit = !!auditMode?.hideInlineHeader;

  const questionOptions = useMemo(() => {
   return question?.options
    ? question?.options
    : question?.question_option_template
    ? question?.question_option_template
    : [];
  }, [question]);

  const filteredResponse = useMemo(() => {
   if (Array.isArray(question?.users_responses)) {
    return question?.users_responses?.find(
     (response) => response?.company?.id_company === +selectedCompanyId
    );
   }

   if (question?.users_responses && typeof question.users_responses === 'object') {
    return question.users_responses;
   }

   return question?.user_responses || null;
  }, [question, selectedCompanyId]);

  const response = filteredResponse?.response || null;

  const gridResponse = useMemo(() => {
    if (type === 'grid' && response) {
      try {
        return typeof response === 'string' ? JSON.parse(response) : response;
      } catch (e) {
        console.error('Erro ao parsear resposta grid:', e);
        return {};
      }
    }
    return {};
  }, [type, response]);

  const selectedOptions = useMemo(() => {
   if (response && (type === 'multiple_choice' || type === 'multi_choice')) {
    let responseParts = [];
    let canBeOld = false;

    if (response?.includes('||')) {
     responseParts = response?.split('||').map((part) => part.trim());
    } else if (response?.includes(',') && !response?.includes('||')) {
     canBeOld = true;
     responseParts = response?.split(',').map((part) => part.trim());
    } else {
     responseParts = [response?.trim()];
    }

    let options = questionOptions.filter((option) =>
     responseParts.includes(option.description.trim())
    );

    if (options.length === 0 && canBeOld) {
     options = questionOptions.filter((option) => response?.includes(option.description));
    }

    return options;
   }
   return [];
  }, [response, type, questionOptions]);

  // EG-63: cor do rail (barra à esquerda do cartão) pelo estado da questão.
  const respondida =
   !!filteredResponse &&
   (String(filteredResponse.response ?? '').trim() !== '' ||
    (filteredResponse.response_file?.length ?? 0) > 0 ||
    respostaTemMedida(filteredResponse));
  const evidenciaRecusada = (filteredResponse?.response_file ?? []).some(
   (f) => f?.approved === false
  );
  const railColor = corDoRail({
   respondida,
   temNota: filteredResponse?.score !== null && filteredResponse?.score !== undefined,
   evidenciaRecusada,
  });

  const auditContainer = {
   spacing: '18px',
   p: AUDIT.padCard,
   pb: AUDIT.padCard,
   bg: AUDIT.surface,
   borderRadius: AUDIT.radius,
   my: AUDIT.gapCard,
   border: `1px solid ${AUDIT.line}`,
   borderLeft: `3px solid ${railColor}`,
   boxShadow: AUDIT.shadow,
   pos: 'relative',
  };
  const legacyContainer = {
   spacing: ['16px', '6'],
   p: '6',
   pb: '26px',
   bg: 'white',
   borderRadius: '12px',
   my: '16px',
   border: '2px solid #EAEEF0',
   pos: 'relative',
  };

  return (
   <Stack {...(isAudit ? auditContainer : legacyContainer)} {...rest}>
    {isAudit && (
     <Flex align='center' gap='10px' mb='2px'>
      <Text
       fontSize='12px'
       fontWeight={700}
       color={AUDIT.inkFaint}
       sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
       {Number.isInteger(index) ? String(index + 1).padStart(2, '0') : ''}
      </Text>
      {question?.is_required && (
       <Text
        sx={AUDIT_LABEL}
        color={AUDIT.statusAttention}
        borderBottom={`1px dotted ${AUDIT.statusAttention}`}
       >
        Obrigatória
       </Text>
      )}
      {!respondida && (
       <Text sx={AUDIT_LABEL} color={AUDIT.statusUnanswered}>
        Não respondida
       </Text>
      )}
     </Flex>
    )}
    {isResponsibleUser && questionnaireType === 'certification' ? (
     <>
      <FeedbackChatReviewerPopover
       response={filteredResponse}
       idSurvey={surveyId}
       currentAdminRole={3}
       question={question}
      //  isSingle={false}
      />
      {/* <ChatAnswersPopover
       openChat={openChat}
       question={question}
       surveyId={surveyId}
       companyId={selectedCompanyId}
       type={questionnaireType}
       userId={userId}
       {...(themeId && { themeId })}
      /> */}
     </>
    ) : isResponsibleUser ? (
     <FeedbackChatReviewerPopover
      response={filteredResponse}
      idSurvey={surveyId}
      currentAdminRole={userId}
      question={question}
     />
    ) : (
     <ChatAnswersPopover
      openChat={openChat}
      question={question}
      surveyId={surveyId}
      companyId={selectedCompanyId}
      type={questionnaireType}
      userId={userId}
      {...(themeId && { themeId })}
     />
    )}

    <Box maxW={isAudit ? '100%' : '86%'} pr={isAudit ? '40px' : 0}>
     {!isAudit && (
      <Text
       as='span'
       color='orange.100'
       fontSize='12px'
       lineHeight='24px'
       fontWeight={500}
       display='block'
      >
       Pergunta obrigatória*
      </Text>
     )}

     <Text
      lineHeight={isAudit ? '1.6' : '32px'}
      mt='4px'
      fontSize={isAudit ? '15px' : undefined}
      color={isAudit ? AUDIT.ink : undefined}
     >
      <Text as='span' fontWeight={700} color={isAudit ? AUDIT.inkFaint : undefined}>
       {code}
      </Text>
      {' - '}
      <Text
       as='span'
       fontWeight={500}
       dangerouslySetInnerHTML={{ __html: description }}
      />
     </Text>

     {/* GRI-08: "Pilar > Tema", como no formulário do respondente. Fica FORA
         do <Text> acima de propósito: aquele nó usa dangerouslySetInnerHTML e
         não aceita filhos. O payload deste endpoint passou a mandar
         id_pillar/id_theme nesta story — antes não havia o que exibir. */}
     <RotuloClassificacao
      idPillar={question?.id_pillar}
      idTheme={question?.id_theme}
      pillarName={question?.pillar_name}
      themeTitle={question?.theme_title}
      ocultarVazio
     />
    </Box>

    {helper && (
     <QuestionHelper
      description={helper?.description || ''}
      videoUrl={helper?.url_video}
     />
    )}

    {!filteredResponse && type !== 'grid' && !isAudit && (
     <Text fontSize='sm' fontWeight={500} color='#EA582B' lineHeight='16.44px'>
      Pergunta ainda não respondida
     </Text>
    )}

    {isAudit && (filteredResponse || type === 'grid') && (
     <Text sx={AUDIT_LABEL} display='block' mt='4px'>
      Resposta da empresa
     </Text>
    )}

    {type === 'grid' && question.gridRows && (question.gridColumns || questionOptions) && (
      <Box>
        {!filteredResponse && (
          <Text fontSize='sm' fontWeight={500} color='#EA582B' lineHeight='16.44px' mb='4'>
            Pergunta ainda não respondida
          </Text>
        )}
        <Box overflowX='auto' border='1px solid' borderColor='gray.200' borderRadius='md'>
          <Table variant='simple' size='sm'>
            <Thead bg='gray.50'>
              <Tr>
                <Th w='200px'></Th>
                {(question.gridColumns || questionOptions)?.map((col) => (
                  <Th
                    key={col.id_question_option_template}
                    textAlign='center'
                    verticalAlign='bottom'
                    fontSize='xs'
                    px='2'
                  >
                    {col.description}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {question.gridRows?.map((row) => (
                <Tr key={row.id_question_grid_row}>
                  <Td fontWeight='500' fontSize='sm' px='3'>
                    {row.description}
                  </Td>
                  {(question.gridColumns || questionOptions)?.map((col) => {
                    const isSelected = gridResponse[`row_${row.id_question_grid_row}`] === col.id_question_option_template?.toString();
                    return (
                      <Td key={col.id_question_option_template} textAlign='center' px='2'>
                        <Radio
                          // EG-60: linha de cima já trata este mesmo campo como
                          // opcional (`?.`) — sem o mesmo cuidado aqui, uma coluna
                          // sem id (template legado com question_option_template
                          // incompleto) derrubava o render inteiro da questão em
                          // vez de só ficar com o rádio sem valor.
                          value={col.id_question_option_template?.toString()}
                          isChecked={isSelected}
                          isReadOnly
                          cursor='not-allowed'
                        />
                      </Td>
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    )}

    {filteredResponse && type !== 'grid' && (
     <Box>
      {type === 'text' && (
       <Flex
        minHeight='48px'
        height='fit-content'
        color={isAudit ? AUDIT.ink : 'black.100'}
        bg={isAudit ? AUDIT.quote : '#F5F5FB'}
        fontSize='sm'
        fontWeight={500}
        lineHeight={isAudit ? '1.6' : '18px'}
        border={isAudit ? `1px solid ${AUDIT.line}` : '1px solid #F5F5FB'}
        borderLeft={isAudit ? `2px solid ${AUDIT.lineStrong}` : undefined}
        borderRadius={isAudit ? AUDIT.radiusSm : '8px'}
        py='14px'
        px='16px'
       >
        <Text
         as='span'
         lineHeight={isAudit ? '1.6' : '18px'}
         fontWeight={500}
         dangerouslySetInnerHTML={{ __html: filteredResponse?.response }}
        />
       </Flex>
      )}

      {type === 'multi_choice' &&
       (isAudit ? (
        <SelectedChips options={selectedOptions} />
       ) : (
        <RadioGroup defaultValue={selectedOptions[0]?.description || ''}>
         <Flex gap='12px' flexWrap='wrap' className='questionary-app'>
          {questionOptions?.map(
           (option, i) =>
            option?.description && (
             <Radio key={i} value={option?.description} isReadOnly>
              {option.description}
             </Radio>
            )
          )}
         </Flex>
        </RadioGroup>
       ))}

      {type === 'multiple_choice' &&
       (isAudit ? (
        <SelectedChips options={selectedOptions} />
       ) : (
        <Flex gap='12px' flexWrap='wrap' className='questionary-app'>
         {questionOptions?.map(
          (option, i) =>
           option?.description && (
            <Checkbox
             key={i}
             value={option?.description}
             isChecked={selectedOptions.some(
              (selected) => selected.description === option.description
             )}
             isReadOnly
             className='questionary-checkbox'
            >
             {option.description}
            </Checkbox>
           )
         )}
        </Flex>
       ))}

      {type === 'number' && (
       <NumericFormat
        value={filteredResponse?.response}
        displayType='text'
        className='number_input'
       />
      )}

      {type === 'money' && (
       <NumericFormat
        value={filteredResponse?.response}
        displayType='text'
        thousandSeparator=','
        decimalSeparator='.'
        prefix='R$ '
        decimalScale={3}
        className='number_input'
       />
      )}

      {type === 'percentage' && (
       <NumericFormat
        value={filteredResponse?.response}
        suffix='%'
        displayType='text'
        maxLength={4}
        fixedDecimalScale
        className='number_input'
       />
      )}

      {filteredResponse?.response_file?.length > 0 &&
       (isAudit ? (
        <Button
         onClick={() => handleQuestionEvidence(question?.id_question, filteredResponse)}
         leftIcon={<Box as={FiPaperclip} boxSize='14px' />}
         variant='ghost'
         size='sm'
         h='32px'
         mt='12px'
         px='10px'
         fontSize='13px'
         fontWeight={600}
         color={AUDIT.accentInk}
         borderRadius={AUDIT.radiusSm}
         _hover={{ bg: AUDIT.accentSoft, opacity: 1 }}
        >
         {filteredResponse.response_file.length} evidência
         {filteredResponse.response_file.length > 1 ? 's' : ''} anexada
         {filteredResponse.response_file.length > 1 ? 's' : ''}
        </Button>
       ) : (
        <Button
         variant='secondary'
         fontSize='15px'
         width='224px'
         height='49px'
         mt='20px'
         color='#0073BB'
         onClick={() => handleQuestionEvidence(question?.id_question, filteredResponse)}
        >
         Visualizar evidências
        </Button>
       ))}
     </Box>
    )}

    {/* GRI-07: fora do `filteredResponse && ...` de propósito — questão ainda
        sem resposta também tem indicador vinculado, e precisa mostrar isso
        (sempreVisivel lista pelo catálogo da questão, "—" pro que falta).
        GRI-09: em modo de auditoria (canScore), "—" vira campo — o avaliador
        preenche o indicador que a empresa deixou vazio, ou corrige o que ela
        informou, mesmo endpoint da nota (PATCH .../gri-value). */}
    {isAudit ? (
     <Box
      bg={AUDIT.panel}
      border={`1px solid ${AUDIT.line}`}
      borderRadius={AUDIT.radiusSm}
      p='16px'
      mt='4px'
     >
      <Text sx={AUDIT_LABEL} mb='14px' display='block'>
       Avaliação do auditor
      </Text>
      <MedidasGri
       gri={question?.gri}
       valores={filteredResponse?.gri_values}
       sempreVisivel
       editavel={auditMode?.canScore}
       responseId={filteredResponse?.id_question_response}
       idReviewer={userId}
       isDisabled={auditMode?.isScoreDisabled}
       flat
      />
      {auditMode?.canScore && (
       <QuestionScoreField
        responseId={filteredResponse?.id_question_response}
        initialScore={filteredResponse?.score}
        isDisabled={auditMode?.isScoreDisabled}
       />
      )}
      {auditMode?.canScore && filteredResponse?.response_file?.length > 0 && (
       <QuestionEvidenceVerdict
        files={filteredResponse.response_file}
        isDisabled={auditMode?.isScoreDisabled}
       />
      )}
     </Box>
    ) : (
     <MedidasGri
      gri={question?.gri}
      valores={filteredResponse?.gri_values}
      sempreVisivel
      editavel={auditMode?.canScore}
      responseId={filteredResponse?.id_question_response}
      idReviewer={userId}
      isDisabled={auditMode?.isScoreDisabled}
     />
    )}
   </Stack>
  );
 }
);

export default QuestionCardSimple;
